// SYMS — School Year Management System — routing and theme
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Login from './components/Login';
import MicrosoftCallback from './components/MicrosoftCallback';
import Dashboard from './components/Dashboard';
import StudentList from './components/StudentList';
import StudentDetailView from './components/StudentDetailView';
import StudentEditForm from './components/StudentEditForm';
import StudentRosterPage from './components/StudentRosterPage';
import StudentRosterList from './components/StudentRosterList';
import MedicalDueReports from './components/MedicalDueReports';
import IncidentLog from './components/IncidentLog';
import AttendancePage from './components/AttendancePage';
import EmployeeList from './components/EmployeeList';
import EmployeeDetailView from './components/EmployeeDetailView';
import EmployeeEditForm from './components/EmployeeEditForm';
import ComplianceReports from './components/ComplianceReports';
import MFASetup from './components/MFASetup';
import MFAVerify from './components/MFAVerify';
import PasswordResetRequest from './components/PasswordResetRequest';
import PasswordResetConfirm from './components/PasswordResetConfirm';
import UserManagement from './components/UserManagement';
import Sessions from './components/Sessions';
import SchoolSessionSetup from './components/SchoolSessionSetup';
import PacketTemplates from './components/PacketTemplates';
import ParentPacketPage from './components/ParentPacketPage';
import AccessDenied from './components/AccessDenied';
import TransportationList from './components/Transportation/TransportationList';
import TransportationForm from './components/Transportation/TransportationForm';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import PrivateRoute from './components/PrivateRoute';
import { COMPANY_NAME, APP_VERSION } from './constants';
import { Typography, Box } from '@mui/material';
import './App.css';

const theme = createTheme({
  palette: {
    primary: {
      main: '#37474f',
    },
    secondary: {
      main: '#dc004e',
    },
  },
});

function AppRoutes() {
  const { user, loading } = useAuth();

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <Routes>
      <Route path="/login" element={!user ? <Login /> : <Navigate to="/" />} />
      <Route path="/login/callback" element={<MicrosoftCallback />} />
      <Route path="/access-denied" element={<AccessDenied />} />
      <Route path="/password-reset" element={<PasswordResetRequest />} />
      <Route path="/password-reset/confirm/:uid/:token" element={<PasswordResetConfirm />} />
      <Route path="/packet/:token" element={<ParentPacketPage />} />
      <Route
        path="/mfa/verify"
        element={
          <PrivateRoute skipMfaCheck>
            <MFAVerify />
          </PrivateRoute>
        }
      />
      <Route
        path="/mfa/setup"
        element={
          <PrivateRoute skipMfaCheck>
            <MFASetup />
          </PrivateRoute>
        }
      />
      <Route
        path="/"
        element={
          <PrivateRoute>
            <Dashboard />
          </PrivateRoute>
        }
      />
      <Route
        path="/students"
        element={
          <PrivateRoute>
            <StudentList />
          </PrivateRoute>
        }
      />
      <Route
        path="/students/roster"
        element={
          <PrivateRoute>
            <StudentRosterPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/students/roster-list"
        element={
          <PrivateRoute>
            <StudentRosterList />
          </PrivateRoute>
        }
      />
      <Route
        path="/reports/medical-due"
        element={
          <PrivateRoute>
            <MedicalDueReports />
          </PrivateRoute>
        }
      />
      <Route
        path="/incidents"
        element={
          <PrivateRoute>
            <IncidentLog />
          </PrivateRoute>
        }
      />
      <Route
        path="/attendance"
        element={
          <PrivateRoute>
            <AttendancePage />
          </PrivateRoute>
        }
      />
      <Route
        path="/students/:id"
        element={
          <PrivateRoute>
            <StudentDetailView />
          </PrivateRoute>
        }
      />
      <Route
        path="/students/:id/edit"
        element={
          <PrivateRoute>
            <StudentEditForm />
          </PrivateRoute>
        }
      />
      <Route
        path="/students/new"
        element={
          <PrivateRoute>
            <StudentEditForm />
          </PrivateRoute>
        }
      />
      <Route
        path="/employees"
        element={
          <PrivateRoute>
            <EmployeeList />
          </PrivateRoute>
        }
      />
      <Route
        path="/employees/new"
        element={
          <PrivateRoute>
            <EmployeeEditForm />
          </PrivateRoute>
        }
      />
      <Route
        path="/employees/:id/edit"
        element={
          <PrivateRoute>
            <EmployeeEditForm />
          </PrivateRoute>
        }
      />
      <Route
        path="/employees/:id"
        element={
          <PrivateRoute>
            <EmployeeDetailView />
          </PrivateRoute>
        }
      />
      <Route
        path="/transportation"
        element={
          <PrivateRoute>
            <TransportationList />
          </PrivateRoute>
        }
      />
      <Route
        path="/transportation/new"
        element={
          <PrivateRoute>
            <TransportationForm />
          </PrivateRoute>
        }
      />
      <Route
        path="/transportation/:studentId"
        element={
          <PrivateRoute>
            <TransportationForm />
          </PrivateRoute>
        }
      />
      <Route
        path="/compliance"
        element={
          <PrivateRoute requiredRole="admin">
            <ComplianceReports />
          </PrivateRoute>
        }
      />
      <Route
        path="/admin/users"
        element={
          <PrivateRoute requiredRole="admin">
            <UserManagement />
          </PrivateRoute>
        }
      />
      <Route
        path="/admin/sessions"
        element={
          <PrivateRoute requiredRole="admin">
            <Sessions />
          </PrivateRoute>
        }
      />
      <Route
        path="/admin/school-session-setup"
        element={
          <PrivateRoute requiredRole="admin">
            <SchoolSessionSetup />
          </PrivateRoute>
        }
      />
      <Route
        path="/admin/packet-templates"
        element={
          <PrivateRoute requiredRole="admin">
            <PacketTemplates />
          </PrivateRoute>
        }
      />
    </Routes>
  );
}

function AppFooter() {
  return (
    <Box
      component="footer"
      sx={{
        py: 2,
        mt: 'auto',
        textAlign: 'center',
      }}
    >
      <Typography variant="caption" color="text.disabled">
        &copy; {new Date().getFullYear()} {COMPANY_NAME}. All rights reserved. v{APP_VERSION}
      </Typography>
    </Box>
  );
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Router>
          <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
            <Box sx={{ flex: 1 }}>
              <AppRoutes />
            </Box>
            <AppFooter />
          </Box>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
